import React from 'react'
import { GetServerSideProps } from 'next'
import { urls } from '../src/utils/config'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles, Grid, Typography, Button, Link } from '@material-ui/core'

import FiltersProvider from '../src/contexts/Filters'
import Nav from '../src/components/Nav'
import ProductNav from '../src/components/Product/Nav'
import ExchangeGuide from '../src/components/Product/exchanges/dexchange/ExchangeGuide'
import FAQSection from '../src/components/Product/FAQSection'
import LearnMore from '../src/components/Product/LearnMore'
import QuickLinks from '../src/components/Footer/QuickLinks'
import Footer from '../src/components/Footer'
import TopTenExchanges from '../src/components/Product/exchanges/dexchange/ExchangesTable'

// Types
import { Exchange } from '../types/exchange'

// API
import { getExchanges } from '../src/services/exchanges'

export interface Props {
  decentralizedExchanges: Exchange[]
  dexAggregators: Exchange[]
  advancedDecentralizedExchanges: Exchange[]
}

const faq = [
  {
    question: 'What is a decentralized exchange?',
    answer:
      'Decentralized exchanges allow users to swap cryptocurrency assets by using smart contracts and on-chain transactions to reduce or eliminate the need for an intermediary. Instead of having to go through an exchange where you have to identify yourself and give-up custody over your assets a DEX lets you remain in control.',
  },
  {
    question: 'Who controls a decentralized exchange?',
    answer:
      'The smart contracts powering a decentralized exchange are “immutable” meaning they exist on the Blockchain forever and no one can change them. The fees go to liquidity providers and sometimes to the decentralized organization (DAO) that developed the contracts.',
  },
  {
    question: 'Are there fees involved for a decentralized exchange?',
    answer:
      'There are two kinds of fees involved. Liquidity provider fees which go to the people who sell the asset to you and network fees which go to the validators securing the Blockchain you are transacting on.',
  },
  {
    question: 'What is a DEX aggregator?',
    answer:
      'A DEX aggregator is a platform that scans all decentralized exchanges for you to find you the best price on the market. Typically, the network fees are a bit higher but if you’re trading large amounts the savings on the token price can be worth the higher network cost.',
  },
  {
    question: 'Why are decentralized exchanges popular?',
    answer:
      'On a decentralized exchange anyone can list any token for trading and anyone can buy a token from anywhere in the world. There is no centralized gatekeeper that prevents you from listing your own token. Moreover, because everything happens on-chain there are efficiency gains which translate into lower costs for end-users.',
  },
]

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    background: 'white',
  },
  heroSection: {
    background: 'linear-gradient(180deg, rgba(209, 197, 224, 0.2) 0.13%, #FFFFFF 100%)',
    overflow: 'auto',
  },
  section: {
    padding: '30px 20px',
    margin: '4rem auto',
    [theme.breakpoints.up('md')]: {
      padding: '30px 80px',
    },
    [theme.breakpoints.up('xl')]: {
      padding: '30px 0',
      maxWidth: '1200px',
    },
    '&>div': {
      padding: '0 20px',
    },
    '& h1': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '54px',
      },
    },
    '& h1, & h3': {
      letterSpacing: '-0.055em',
      margin: '1rem 0',
    },
    '& p': {
      fontSize: '16px',
      color: '#7D7787',
    },
  },
  heroContentSection: {},
  heroCTA: {
    textTransform: 'unset',
    margin: '1rem 1rem 1rem 0',
  },
  link: {
    color: 'inherit',
    fontSize: '.8rem',
    padding: '.4rem 0',
    borderBottom: '1px solid',
  },
  videoWrapper: {
    position: 'relative',
    height: '360px',
    overflow: 'hidden',
    borderRadius: '1rem',

    '& iframe': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
  },
  purpleText: {
    color: theme.palette.primary.main,
  },
}))

const Exchanges = ({
  decentralizedExchanges,
  dexAggregators,
  advancedDecentralizedExchanges,
}: Props): JSX.Element => {
  const classes = useStyles({})

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.productExchanges,
        },
      })}
    >
      {(): JSX.Element => (
        <FiltersProvider>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters.com - Cryptocurrency Exchanges`}
            description={`Cryptotesters.com | Crypto Exchanges allow you to buy at the best market rate cryptocurrency using conventional payment methods like by bank transfer or credit card.`}
            openGraph={{
              url: `https://cryptotesters.com/product/crypto-exchanges`,
              title: `Cryptotesters.com - Cryptocurrency Exchanges`,
              description: `Cryptotesters.com | Crypto Exchanges allow you to buy at the best market rate cryptocurrency using conventional payment methods like by bank transfer or credit card.`,
              images: [
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/large/home.jpg`,
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/small/home.jpg`,
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <div className={classes.pageWrapper}>
            <div className={classes.heroSection}>
              <Nav dark />
              <ProductNav />

              <Grid container className={classes.section}>
                <Grid item md={5} xs={12}>
                  <Typography variant="h1">
                    The best <span className={classes.purpleText}>decentralized</span> exchanges
                  </Typography>
                  <Typography variant="body2">
                    Decentralized exchanges allow you to trade directly on the Blockchain from your
                    Ethereum wallet retaining 100% control over your funds. Swaps are settled by
                    so-called “smart contracts” without any central intermediary involved.
                  </Typography>

                  <Button
                    color="primary"
                    variant="contained"
                    href="#exchanges"
                    className={classes.heroCTA}
                  >
                    Compare Exchanges
                  </Button>
                  <Link className={classes.link} underline="none">
                    See the whole guide
                  </Link>
                </Grid>

                <Grid item md={7} xs={12}>
                  <div>
                    <div className={classes.videoWrapper}>
                      <iframe
                        width="560"
                        height="315"
                        src="https://www.youtube.com/embed/GE1-FK1-YFw"
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>

            <div id="exchanges">
              <TopTenExchanges
                exchanges={[
                  ...decentralizedExchanges,
                  ...dexAggregators,
                  ...advancedDecentralizedExchanges,
                ]}
              />
              <ExchangeGuide />
              <FAQSection faqs={faq} />
              <LearnMore />
            </div>
            <QuickLinks />
            <Footer />
          </div>
        </FiltersProvider>
      )}
    </Amplitude>
  )
}

export default Exchanges

export const getServerSideProps: GetServerSideProps = async () => {
  const allExchanges: Record<string, Exchange[]> = {}

  try {
    const exchanges = await getExchanges([
      'dex-aggregators',
      'advanced-decentralized-exchanges',
      'decentralized-exchanges',
    ])
    allExchanges.dexAggregators = exchanges.filter((obj) => obj.category.code === 'dex-aggregators')
    allExchanges.advancedDecentralizedExchanges = exchanges.filter(
      (obj) => obj.category.code === 'advanced-decentralized-exchanges',
    )
    allExchanges.decentralizedExchanges = exchanges.filter(
      (obj) => obj.category.code === 'decentralized-exchanges',
    )
  } catch (e) {
    console.error(e)
  }
  4
  return {
    props: allExchanges,
  }
}
